import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { Skeleton } from '../../../components/ui/skeleton';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

import { 
  Zap, 
  Globe, 
  Database, 
  Server, 
  TrendingUp, 
  TrendingDown,
  Activity,
  Clock,
  HardDrive,
  Cpu
} from 'lucide-react';

/**
 * Componente de métricas de performance
 * Muestra gráficos y estadísticas de rendimiento de la aplicación
 */
const PerformanceMetrics = ({ metrics, isLoading, autoRefresh }) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('24h');

  // Generar datos de ejemplo para gráficos
  const generateTimeSeriesData = (metric, days = 7) => {
    const data = [];
    const now = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      
      data.push({
        date: date.toISOString().split('T')[0],
        value: Math.floor(Math.random() * 100) + 50,
        timestamp: date.getTime()
      });
    }
    
    return data;
  };

  // Datos para gráficos
  const responseTimeData = generateTimeSeriesData('responseTime');
  const requestsData = generateTimeSeriesData('requests');
  const errorRateData = generateTimeSeriesData('errorRate');

  // Datos para gráfico de pastel de regiones
  const regionData = [
    { name: 'US East', value: 45, color: '#8884d8' },
    { name: 'Europe', value: 30, color: '#82ca9d' },
    { name: 'Asia Pacific', value: 20, color: '#ffc658' },
    { name: 'Others', value: 5, color: '#ff7300' }
  ];

  // Formatear números
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num?.toString() || '0';
  };

  // Formatear bytes
  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
        
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Métricas principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Requests */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Requests</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(metrics?.requests?.total || 125000)}
            </div>
            <div className="flex items-center text-xs text-[var(--yool-success)] mt-2">
              <TrendingUp className="h-3 w-3 mr-1" />
              +12% vs ayer
            </div>
            <Progress value={75} className="mt-2" />
          </CardContent>
        </Card>

        {/* Bandwidth */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Bandwidth</CardTitle>
            <HardDrive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatBytes(metrics?.bandwidth?.total || 2500000000)}
            </div>
            <div className="flex items-center text-xs text-[var(--yool-success)] mt-2">
              <TrendingUp className="h-3 w-3 mr-1" />
              +8% vs ayer
            </div>
            <Progress value={60} className="mt-2" />
          </CardContent>
        </Card>

        {/* Function Invocations */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Functions</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(metrics?.functions?.invocations || 45000)}
            </div>
            <div className="flex items-center text-xs text-[var(--yool-error)] mt-2">
              <TrendingDown className="h-3 w-3 mr-1" />
              -3% vs ayer
            </div>
            <Progress value={45} className="mt-2" />
          </CardContent>
        </Card>

        {/* Edge Requests */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Edge Cache</CardTitle>
            <Server className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {metrics?.edge?.hitRate || 94}%
            </div>
            <div className="flex items-center text-xs text-[var(--yool-success)] mt-2">
              <TrendingUp className="h-3 w-3 mr-1" />
              Excelente rendimiento
            </div>
            <Progress value={94} className="mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* Gráficos detallados */}
      <Tabs defaultValue="response-time" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="response-time">Response Time</TabsTrigger>
          <TabsTrigger value="requests">Requests</TabsTrigger>
          <TabsTrigger value="errors">Error Rate</TabsTrigger>
          <TabsTrigger value="regions">Regiones</TabsTrigger>
        </TabsList>

        {/* Response Time */}
        <TabsContent value="response-time">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-5 w-5" />
                <span>Response Time</span>
                {autoRefresh && (
                  <Badge variant="secondary" className="ml-2">
                    <Activity className="h-3 w-3 mr-1" />
                    Live
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Tiempo de respuesta promedio en los últimos 7 días
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={responseTimeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(value) => `Fecha: ${value}`}
                    formatter={(value) => [`${value}ms`, 'Response Time']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#8884d8" 
                    strokeWidth={2}
                    dot={{ fill: '#8884d8' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Requests */}
        <TabsContent value="requests">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Globe className="h-5 w-5" />
                <span>Requests por Día</span>
              </CardTitle>
              <CardDescription>
                Volumen de requests en los últimos 7 días
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={requestsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(value) => `Fecha: ${value}`}
                    formatter={(value) => [`${formatNumber(value * 1000)}`, 'Requests']}
                  />
                  <Bar dataKey="value" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Error Rate */}
        <TabsContent value="errors">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Activity className="h-5 w-5" />
                <span>Error Rate</span>
              </CardTitle>
              <CardDescription>
                Tasa de errores en los últimos 7 días
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={errorRateData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(value) => `Fecha: ${value}`}
                    formatter={(value) => [`${(value / 10).toFixed(2)}%`, 'Error Rate']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#ff7300" 
                    strokeWidth={2}
                    dot={{ fill: '#ff7300' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Regiones */}
        <TabsContent value="regions">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Globe className="h-5 w-5" />
                <span>Distribución por Regiones</span>
              </CardTitle>
              <CardDescription>
                Tráfico distribuido por regiones geográficas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={regionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {regionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>

                <div className="space-y-4">
                  {regionData.map((region, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: region.color }}
                        />
                        <span className="font-medium">{region.name}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{region.value}%</p>
                        <p className="text-sm text-muted-foreground">
                          {formatNumber(region.value * 1250)} requests
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Métricas adicionales */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Core Web Vitals */}
        <Card>
          <CardHeader>
            <CardTitle>Core Web Vitals</CardTitle>
            <CardDescription>
              Métricas clave de experiencia de usuario
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">LCP (Largest Contentful Paint)</span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm">1.2s</span>
                  <Badge variant="default">Good</Badge>
                </div>
              </div>
              <Progress value={80} className="h-2" />

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">FID (First Input Delay)</span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm">45ms</span>
                  <Badge variant="default">Good</Badge>
                </div>
              </div>
              <Progress value={90} className="h-2" />

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">CLS (Cumulative Layout Shift)</span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm">0.08</span>
                  <Badge variant="default">Good</Badge>
                </div>
              </div>
              <Progress value={85} className="h-2" />
            </div>
          </CardContent>
        </Card>

        {/* Resource Usage */}
        <Card>
          <CardHeader>
            <CardTitle>Uso de Recursos</CardTitle>
            <CardDescription>
              Consumo de recursos del servidor
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Cpu className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">CPU</span>
                </div>
                <span className="text-sm">45%</span>
              </div>
              <Progress value={45} className="h-2" />

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <HardDrive className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Memory</span>
                </div>
                <span className="text-sm">62%</span>
              </div>
              <Progress value={62} className="h-2" />

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Database className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Storage</span>
                </div>
                <span className="text-sm">28%</span>
              </div>
              <Progress value={28} className="h-2" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PerformanceMetrics;